import { create } from 'zustand';
import type { BrandDto, BrandInput } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';

/**
 * The brands screen (ADR-095) — searched and paged on the server, added and
 * changed here: names, description, logo, search result. A brand opened for
 * editing is read with both languages.
 */
interface BrandsState {
  items: BrandDto[];
  nextCursor: string | null;
  search: string;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;

  load: (filters?: { search?: string }) => Promise<void>;
  loadMore: () => Promise<void>;
  open: (id: string) => Promise<BrandDto | null>;
  save: (id: string | null, input: BrandInput) => Promise<boolean>;
}

const PAGE_SIZE = 20;
let listRequest = 0;

export const useBrandsStore = create<BrandsState>((set, get) => ({
  items: [],
  nextCursor: null,
  search: '',
  loading: false,
  error: null,
  saving: false,
  saveError: null,

  async load(filters = {}) {
    const search = filters.search ?? get().search;
    const request = ++listRequest;
    set({ loading: true, error: null, search });
    try {
      const page = await withSession(() =>
        adminApi.catalog.brands({ limit: PAGE_SIZE, includeInactive: true, locale: useUiStore.getState().locale, ...(search.trim() ? { search: search.trim() } : {}) }),
      );
      if (request !== listRequest) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      if (request !== listRequest) return;
      set({ loading: false, error: errorMessage(error, 'Could not load the brands.') });
    }
  },

  async loadMore() {
    const { nextCursor, search, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.catalog.brands({
          limit: PAGE_SIZE,
          cursor: nextCursor,
          includeInactive: true,
          locale: useUiStore.getState().locale,
          ...(search.trim() ? { search: search.trim() } : {}),
        }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more brands.') });
    }
  },

  async open(id) {
    try {
      return await withSession(() => adminApi.catalog.brand(id, useUiStore.getState().locale));
    } catch (error) {
      set({ error: errorMessage(error, 'Could not open the brand.') });
      return null;
    }
  },

  async save(id, input) {
    set({ saving: true, saveError: null });
    try {
      const saved = await withSession(() =>
        id ? adminApi.catalog.updateBrand(id, input) : adminApi.catalog.createBrand({ ...input, translations: input.translations! }),
      );
      set((state) => ({
        saving: false,
        items: id ? state.items.map((item) => (item.id === id ? { ...item, ...saved } : item)) : [saved, ...state.items],
      }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the brand.') });
      return false;
    }
  },
}));
